import { createColumnHelper, getSortedRowModel } from '@tanstack/react-table';
import { FlexTableV2 } from 'components/FlexTableV2';
import SimpleHeader from 'components/Headers/SimpleHeader';
import { useAgentsLoggedInChatQueues } from 'hooks/useAgentsLoggedInChatQueues';
import { useMonitoringChatQueuesSocket } from 'hooks/useMonitoringChatQueuesSocket';
import { usePauses } from 'hooks/usePauses';
import useQueues from 'hooks/useQueues';
import React, { useCallback, useMemo, useState } from 'react';
import { FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  UncontrolledTooltip
} from 'reactstrap';
import { LOCAL_STORAGE_KEYS } from 'utils/fuctions/localStorage';
import * as yup from 'yup';
import { LoginAgentsModal } from './ActionModals/LoginAgentsModal';
import { RefreshAgentsLogged } from './ActionModals/RefreshAgentsLogged';
import { SupervisorPanelChatActions } from './SupervisorPanelChatActions';

const columnHelper = createColumnHelper();

export const SupervisorPanelChat = () => {
  const [columnSizing, setColumnSizing] = useState();
  const [columnVisibility, setColumnVisibility] = useState();
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: 'name',
      desc: false
    }
  ]);

  const {
    response: { chat: agentsLoggedInChatQueues, system: agentsLoggedInSystem },
    emitGetAgentsLoggedInSystem,
    emitGetAgentsLoggedInChatQueues
  } = useAgentsLoggedInChatQueues();

  const {
    idMap: queuesIdMap,
    options: queuesOptions,
    loginToAllowedQueuesOptionsMap,
    logoutByAgentLoginAndQueueIds,
    loginByAgentLoginAndQueueIds
  } = useQueues({
    sort: { key: 'name', order: 'asc' }
  });
  const { options: pausesOptions, pauseAgent, unpauseAgent } = usePauses();

  const pausesIdMap = useMemo(
    () =>
      pausesOptions.reduce((map, pause) => {
        map.set(pause.value, pause.label);
        return map;
      }, new Map()),
    [pausesOptions]
  );

  const allQueueNames = useMemo(
    () => queuesOptions.map((q) => q.label),
    [queuesOptions]
  );

  const { response: monitoringData } = useMonitoringChatQueuesSocket({
    queueNames: allQueueNames,
    group: 'agent'
  });

  const handleRefresh = useCallback(() => {
    emitGetAgentsLoggedInSystem();
    emitGetAgentsLoggedInChatQueues();
    toast.info('Atualizando informações dos atendentes...', {
      closeOnClick: true,
      autoClose: 1500
    });
  }, [emitGetAgentsLoggedInChatQueues, emitGetAgentsLoggedInSystem]);

  const monitoringAgentMap = useMemo(() => {
    const agentData = monitoringData?.agent ?? [];
    return agentData.reduce((map, agent) => {
      map.set(agent.agentLogin, agent);
      return map;
    }, new Map());
  }, [monitoringData]);

  const agentsSystemOptions = useMemo(
    () =>
      agentsLoggedInSystem.map((agent) => ({
        value: agent.login,
        label: agent.name
      })),
    [agentsLoggedInSystem]
  );

  const tableData = useMemo(() => {
    const mergedAgents = agentsLoggedInChatQueues.map((agent) => {
      const agentMonitoringData = monitoringAgentMap.get(agent.login);

      const pauseId = agentMonitoringData?.pauseId ?? null;
      const pauseName = pausesIdMap.get(pauseId) || null;
      const chatsInAttendanceCount =
        agentMonitoringData?.chatsInAttendance ?? 0;

      return {
        name: agent.name,
        login: agent.login,
        queueIds: agent.queueIds,
        queues: agent.queueIds
          .map((qid) => queuesIdMap.get(qid)?.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b)),
        pauseId,
        pauseName,
        chatsInAttendance: chatsInAttendanceCount
      };
    });

    const filteredAgents = mergedAgents.filter(
      (agent) => agent.queueIds.length
    );

    const { success, data, error } =
      TableDataSchema.safeValidateSync(filteredAgents);

    if (!success) {
      toast.error(
        error?.message ?? 'Não foi possível validar os dados recebidos'
      );
      console.error(error);
    }

    return data ?? [];
  }, [agentsLoggedInChatQueues, monitoringAgentMap, pausesIdMap, queuesIdMap]);

  const allAgentNames = useMemo(
    () => tableData.map((agent) => agent.name),
    [tableData]
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: 'Atendente',
        enableSorting: true,
        sortingFn: 'text',
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) {
            return true;
          }

          return filterValue.includes(row.getValue(columnId));
        },
        meta: {
          filterVariant: 'multiselect',
          options: allAgentNames
        }
      }),
      columnHelper.accessor('chatsInAttendance', {
        header: 'Em atendimento',
        size: 50,
        enableSorting: true,
        meta: {
          filterVariant: 'range'
        }
      }),
      columnHelper.accessor((row) => row.pauseName || '-', {
        header: 'Pausa',
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) {
            return true;
          }

          const rowPauseName = row.getValue(columnId);

          return filterValue.includes(rowPauseName);
        },
        meta: {
          filterVariant: 'multiselect',
          options: Array.from(pausesIdMap.values())
        }
      }),
      columnHelper.accessor((row) => row.queues, {
        header: 'Filas',
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) {
            return true;
          }

          const agentQueues = row.getValue(columnId);

          if (!Array.isArray(agentQueues)) {
            return false;
          }

          return filterValue.some((queue) => agentQueues.includes(queue));
        },
        meta: {
          filterVariant: 'multiselect',
          options: allQueueNames
        },
        cell: (cell) => {
          if (!cell.row.original.queues?.length)
            return (
              <div className="d-flex justify-content-center">
                <span>Não logado</span>
              </div>
            );

          const safeTargetId = (
            cell.row.original?.agentLogin ?? cell.row.original.login
          )
            ?.replace(/[^a-zA-Z0-9-_]/g, '-')
            .toLowerCase();

          return (
            <div className="d-flex justify-content-center">
              {safeTargetId ? (
                <>
                  <FiInfo size="1.5rem" id={safeTargetId} />
                  <UncontrolledTooltip
                    placement="top"
                    target={safeTargetId}
                    trigger="hover"
                  >
                    <strong>Filas:</strong>
                    <br />
                    {cell.row.original?.queues?.join?.(', ') ||
                      'Nenhuma fila disponível'}
                  </UncontrolledTooltip>
                </>
              ) : (
                <span>Dados indisponíveis</span>
              )}
            </div>
          );
        }
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className="mx-auto">Ações</div>,
        cell: (cell) => {
          return (
            <SupervisorPanelChatActions
              {...cell.row.original}
              {...{
                queuesOptions,
                logoutByAgentLoginAndQueueIds,
                loginByAgentLoginAndQueueIds,
                loginToAllowedQueuesOptionsMap,
                pausesOptions,
                pauseAgent,
                unpauseAgent,
                handleRefresh
              }}
            />
          );
        }
      })
    ];
  }, [
    queuesOptions,
    logoutByAgentLoginAndQueueIds,
    loginByAgentLoginAndQueueIds,
    loginToAllowedQueuesOptionsMap,
    pausesOptions,
    pauseAgent,
    unpauseAgent,
    handleRefresh,
    allAgentNames,
    allQueueNames,
    pausesIdMap
  ]);

  return (
    <>
      <SimpleHeader showBackArrow={'not'} />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">Painel do supervisor - Chat</h3>

                    <div className="d-flex">
                      <LoginAgentsModal
                        agentsOptions={agentsSystemOptions}
                        loginToAllowedQueuesOptionsMap={
                          loginToAllowedQueuesOptionsMap
                        }
                        loginByAgentLoginAndQueueIds={
                          loginByAgentLoginAndQueueIds
                        }
                      />

                      <RefreshAgentsLogged handleRefresh={handleRefresh} />
                    </div>
                  </div>
                </CardHeader>

                <CardBody className="overflow-y-visible">
                  <FlexTableV2
                    table={{
                      columns: columns,
                      data: tableData,
                      columnResizeMode: 'onChange',
                      enableLocalStorageMemoWithKey:
                        LOCAL_STORAGE_KEYS.SUPERVISOR_PANEL_CHAT,
                      getSortedRowModel: getSortedRowModel(),
                      manualSorting: false,
                      manualFiltering: false
                    }}
                    state={{
                      columnSizing,
                      setColumnSizing,
                      columnVisibility,
                      setColumnVisibility,
                      sorting,
                      setSorting,
                      columnFilters,
                      setColumnFilters
                    }}
                  ></FlexTableV2>
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

const TableDataSchema = yup.array(
  yup.object({
    name: yup.string().defined('Nome do atendente não informado'),
    login: yup.string().defined('Login do atendente não informado'),
    pauseId: yup.number().nullable(),
    pauseName: yup.string().nullable(),
    queues: yup
      .array(yup.string())
      .defined('É necessário informar os nomes das filas do atendente'),
    queueIds: yup
      .array(yup.number())
      .defined('É necessário informar os IDs das filas do atendente'),
    chatsInAttendance: yup
      .number()
      .defined('É necessário informar a quantidade de chats em atendimento')
  }),
  'Os dados exibidos na tabela devem ser fornecidos em formato de lista'
);
